import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Upload, FileText, CheckCircle2, AlertTriangle } from "lucide-react";
import AnnualPracticingCertificate from "./Assets/AnnualPracticingCertificate";

const PractisingCh = () => {
    const accessToken = localStorage.getItem("accessToken");
    const currentYear = new Date().getFullYear();

    const [status, setStatus] = useState("checking");
    const [selectedYear, setSelectedYear] = useState(currentYear);

    const [renewals, setRenewals] = useState({});
    const [commentsByYear, setCommentsByYear] = useState({});

    const [goodStanding, setGoodStanding] = useState(null);
    const [additionalAttachment, setAdditionalAttachment] = useState(null);
    const [goodStandingName, setGoodStandingName] = useState("");
    const [additionalAttachmentName, setAdditionalAttachmentName] = useState("");

    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    /* ----------------------------------------
       FETCH STATUS + DATA
    ---------------------------------------- */
    useEffect(() => {
        if (!accessToken) return;

        const fetchData = async () => {
            try {
                setStatus("checking");

                const meRes = await axios.get(
                    "https://portal.lsb.go.ke/account/me/",
                    { headers: { Authorization: `Bearer ${accessToken}` } }
                );

                const licenseNo = meRes.data?.user?.license_no;
                if (!licenseNo) {
                    setStatus("not_applied");
                    return;
                }

                let renewalRes;
                let renewalResError = null;
                try {
                    renewalRes = await axios.get(
                        `https://portal.lsb.go.ke/account/renewal/?license_no=${licenseNo}`,
                        { headers: { Authorization: `Bearer ${accessToken}` } }
                    );
                } catch (err) {
                    renewalResError = err;
                }

                let allRes;
                try {
                    allRes = await axios.get(
                        `https://portal.lsb.go.ke/account/all_renewal/?license_no=${licenseNo}`,
                        { headers: { Authorization: `Bearer ${accessToken}` } }
                    );
                } catch (err) {
                    allRes = { data: [] };
                }

                // If we have a renewalRes and not error, parse as usual, otherwise leave as empty obj
                const approvedData = renewalRes && !renewalResError
                    ? renewalRes.data || {}
                    : {};

                setRenewals(approvedData);

                // Prepare all_renewal list and commentMap using ONLY comments from all_renewal endpoint
                const list = Array.isArray(allRes.data)
                    ? allRes.data
                    : allRes.data?.results || [];

                const commentMap = {};
                list.forEach(r => {
                    if (r.comments && !r.is_approved && r.year) {
                        commentMap[r.year] = commentMap[r.year] || [];
                        commentMap[r.year].push(r.comments);
                    }
                });

                setCommentsByYear(commentMap);

                // For the currently selected year
                const approvedForYear = approvedData[selectedYear];
                const isApproved =
                    approvedForYear?.certificate_number &&
                    approvedForYear?.date_approved;

                const yearComments =
                    Array.isArray(commentMap[selectedYear]) &&
                    commentMap[selectedYear].length > 0;

                const hasRecord = list.some(
                    r => String(r.year) === String(selectedYear)
                );

                if (isApproved) {
                    setStatus("approved");
                } else if (yearComments || hasRecord) {
                    setStatus("under_review");
                } else {
                    setStatus("not_applied");
                }
            } catch (err) {
                console.error("FETCH ERROR:", err);
                setStatus("not_applied");
            }
        };

        fetchData();
    }, [accessToken, selectedYear]);

    /* ----------------------------------------
       FILE HANDLER
    ---------------------------------------- */
    const handleFile = (e, setter, nameSetter) => {
        const file = e.target.files[0];
        if (!file || file.type !== "application/pdf") {
            setErrorMessage("Please upload a valid PDF file.");
            setter(null);
            nameSetter("");
            return;
        }
        setter(file);
        nameSetter(file.name);
        setErrorMessage("");
    };

    /* ----------------------------------------
       SUBMIT APPLICATION
    ---------------------------------------- */
    const handleSubmit = async () => {
        if (!goodStanding || !additionalAttachment) {
            setErrorMessage("Please upload all required documents.");
            return;
        }

        setLoading(true);
        setErrorMessage("");

        try {
            const formData = new FormData();
            formData.append("good_standing", goodStanding);
            formData.append("additional_attachment", additionalAttachment);
            formData.append("year", currentYear);

            await axios.put(
                "https://portal.lsb.go.ke/account/renewal/",
                formData,
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );

            setStatus("under_review");
        } catch (err) {
            setErrorMessage(
                err.response?.data?.details ||
                err.response?.data?.detail ||
                "Submission failed."
            );
        } finally {
            setLoading(false);
        }
    };

    /* ----------------------------------------
       RENDER STATES
    ---------------------------------------- */
    if (status === "checking") {
        return (
            <div className="py-20 text-center text-gray-500">
                Checking status…
            </div>
        );
    }

    /* ---------- APPROVED ---------- */
    if (status === "approved") {
        const cert = renewals[selectedYear];

        return (
            <motion.div className="p-6">
                <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                    <FileText className="text-indigo-600" />
                    Annual Practicing Certificate
                </h3>

                <div className="flex items-center gap-3 mb-6">
                    <span className="font-semibold text-sm">Select Year:</span>

                    <select
                        value={selectedYear}
                        onChange={e => setSelectedYear(Number(e.target.value))}
                        className="block bg-white border border-gray-300 rounded-lg px-4 py-2 pr-10 text-sm font-medium text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition cursor-pointer appearance-none"
                        style={{ minWidth: 110 }}
                    >
                        {[...Array(5)].map((_, i) => {
                            const y = currentYear - i;
                            return (
                                <option key={y} value={y}>
                                    {y}
                                </option>
                            );
                        })}
                    </select>
                </div>

                <AnnualPracticingCertificate
                    year={selectedYear}
                    certificateNumber={cert?.certificate_number}
                    name={cert?.name}
                    licenseNo={cert?.license_no}
                    approvedDate={cert?.date_approved}
                    signature={cert?.signature}
                />
            </motion.div>
        );
    }

    /* ---------- UNDER REVIEW ---------- */
    if (status === "under_review") {
        const comments = commentsByYear[selectedYear] || [];
        const displayedComments = [];
        if (comments.length > 0) {
            displayedComments.push(...comments);
        }

        return (
            <motion.div className="p-8 text-center">
                <AlertTriangle className="mx-auto text-amber-500 w-12 h-12 mb-3" />
                <h3 className="font-bold mb-2">Under Review</h3>

                <p className="text-md text-slate-700 max-w-lg mx-auto mb-3">
                    Your application has been received, however, the board would like you to make the following ammendments for your application to be approved.
                </p>

                {displayedComments.length > 0 ? (
                    <div className="max-w-xl mx-auto text-left">
                        {displayedComments.map((c, i) => (
                            <p key={i} className="text-sm mb-2">
                                <b className="text-red-600">*</b> {c}
                            </p>
                        ))}
                    </div>
                ) : (
                    <p className="text-gray-500">No comments available.</p>
                )}
            </motion.div>
        );
    }

    /* ---------- NOT APPLIED ---------- */
    return (
        <motion.div className="p-8">
            <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <Upload className="text-indigo-600" />
                Practising License Application
            </h3>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
                {[{
                    label: "Good Standing Letter",
                    file: goodStanding,
                    name: goodStandingName,
                    setter: setGoodStanding,
                    nameSetter: setGoodStandingName,
                    id: "goodStanding"
                },
                {
                    label: "Additional Attachment",
                    file: additionalAttachment,
                    name: additionalAttachmentName,
                    setter: setAdditionalAttachment,
                    nameSetter: setAdditionalAttachmentName,
                    id: "additionalAttachment"
                }].map(f => (
                    <label
                        key={f.id}
                        className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-6 cursor-pointer
            ${f.name ? "border-emerald-500 bg-emerald-50" : "border-slate-300 hover:border-indigo-400"}`}
                    >
                        {f.name ? (
                            <CheckCircle2 className="w-10 h-10 text-emerald-500 mb-2" />
                        ) : (
                            <Upload className="w-10 h-10 text-indigo-400 mb-2" />
                        )}

                        <span className="font-semibold">{f.label}</span>
                        <span className="text-xs text-gray-500">PDF only</span>

                        <input
                            type="file"
                            accept=".pdf"
                            className="hidden"
                            onChange={e => handleFile(e, f.setter, f.nameSetter)}
                        />

                        {f.name && (
                            <span className="mt-2 text-xs bg-emerald-100 px-2 py-1 rounded">
                                {f.name}
                            </span>
                        )}
                    </label>
                ))}
            </div>

            {errorMessage && (
                <p className="mb-4 text-red-600">* {errorMessage}</p>
            )}

            <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-6 py-3 bg-[#3d0707] text-white rounded disabled:opacity-50"
            >
                {loading ? "Submitting…" : "Submit Application"}
            </button>

            <div className="pt-4">
                <p className="font-medium"><b className="underline underline-offset-4">Note:</b> The application for renewal should include:</p>

                <div className="flex flex-col">
                    <span><b className="text-[#ff0000]">*</b> Letter of application</span>
                    <span><b className="text-[#ff0000]">*</b> Payment Transaction Codes</span>
                    <span><b className="text-[#ff0000]">*</b> List of assistants</span>
                </div>
            </div>
        </motion.div>
    );
};

export default PractisingCh;
